import React, { useState, useMemo } from 'react';

interface LoadChartModalProps {
  chartTitles: string[];
  chartsWithRecording?: Set<string>;
  onLoad: (title: string) => void;
  onDelete: (title: string) => void;
  onClose: () => void;
  onRefresh?: () => Promise<void>;
  isRefreshing?: boolean;
  onImportJSON?: () => void;
  onExportJSON?: () => void;
}

const LoadChartModal: React.FC<LoadChartModalProps> = ({ chartTitles, chartsWithRecording, onLoad, onDelete, onClose, onRefresh, isRefreshing = false, onImportJSON, onExportJSON }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter chart titles based on search query
  const filteredChartTitles = useMemo(() => {
    if (!searchQuery.trim()) {
      return chartTitles;
    }
    return chartTitles.filter(title => 
      title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [chartTitles, searchQuery]);
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 clickable-area"
      onClick={onClose}
    >
      <div 
        className="rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
        style={{ backgroundColor: '#161616' }}
        onClick={e => e.stopPropagation()}
      >
        <header className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Open Chart</h2>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <button 
                onClick={onRefresh}
                disabled={isRefreshing}
                className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Refresh charts from cloud"
                title="Sync with Google Drive"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>
        
        {/* Search Bar */}
        {chartTitles.length > 0 && (
          <div className="p-4 border-b border-gray-700">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search charts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="mt-2 text-sm text-gray-400">
                {filteredChartTitles.length} of {chartTitles.length} charts
              </div>
            )}
          </div>
        )}
        
        {/* Import / Export footer */}
        {(onImportJSON || onExportJSON) && (
          <footer className="flex items-center gap-2 px-4 py-3 border-t border-gray-700">
            {onImportJSON && (
              <button
                onClick={onImportJSON}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors border border-gray-700"
                title="Import a chart from a JSON file"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                Import JSON
              </button>
            )}
            {onExportJSON && (
              <button
                onClick={onExportJSON}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors border border-gray-700"
                title="Export current chart as JSON file"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export JSON
              </button>
            )}
          </footer>
        )}

        <main className="p-4 overflow-y-auto">
          {chartTitles.length === 0 ? (
            <p className="text-gray-400 text-center py-8">You have no saved charts.</p>
          ) : filteredChartTitles.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-2">No charts found for "{searchQuery}"</p>
              <button
                onClick={() => setSearchQuery('')}
                className="text-cyan-400 hover:text-cyan-300 text-sm"
              >
                Clear search
              </button>
            </div>
          ) : (
            <ul className="space-y-2">
              {filteredChartTitles.map(title => (
                <li key={title} className="flex justify-between items-center bg-gray-700 p-3 rounded-md">
                  <span className="text-gray-200 font-semibold flex items-center gap-2">
                    {title}
                    {chartsWithRecording?.has(title) && (
                      <span title="Partition piano enregistrée" aria-label="Partition piano enregistrée">🎹</span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => onLoad(title)}
                      className="px-4 py-1 text-sm rounded-md bg-cyan-600 hover:bg-cyan-500 text-white font-semibold transition-colors"
                    >
                      Load
                    </button>
                    <button 
                      onClick={() => onDelete(title)}
                      className="p-2 rounded-md bg-gray-600 hover:bg-red-500 text-gray-300 hover:text-white transition-colors"
                      aria-label={`Delete ${title}`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </main>
      </div>
    </div>
  );
};

export default LoadChartModal;
